import { AxiosError } from "axios";
import { ExternalLink, FileText, Trash2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { highlightText } from "@/utils/highlightText";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { getSpotFileUrl, spotFileId } from "@/utils/generic";
import { pickIcon } from "@/components/NewAndIncredibleChatWidget/PresentedFileWidget";

// Room for the pinned controls: 2 × (size-3.5 icon + p-1) + gap-0.5 = 2.875rem,
// rounded to 3rem, with the name fading over the 1.5rem before them.
const NAME_FADE_BEHIND_CONTROLS =
  "group-hover/file:[mask-image:linear-gradient(to_right,black_calc(100%_-_4.5rem),transparent_calc(100%_-_3rem))] " +
  "group-focus-within/file:[mask-image:linear-gradient(to_right,black_calc(100%_-_4.5rem),transparent_calc(100%_-_3rem))]";

// Same shape as the grid's other icon-only hover controls (TableGrid row actions).
const CONTROL = "cursor-pointer rounded p-1 text-muted-foreground";

export function FileCell({
  uri,
  onRemove,
  searchTerm,
  typeIcon = false,
}: {
  uri: string;
  onRemove?: () => void;
  searchTerm?: string;
  /** Show the file's content-type icon instead of the plain file icon. */
  typeIcon?: boolean;
}) {
  const isSpotUri = uri.startsWith("spot://");
  const fileId = spotFileId(uri);
  const { data, isError } = useRemoteData(
    DATA_KEYS.FILE_METADATA,
    { path: { name: fileId ?? "" } },
    {
      enabled: fileId != null,
      retry: (count, error) =>
        !(error instanceof AxiosError && error.response?.status === 404) &&
        count < 2,
    }
  );

  const unavailable = isSpotUri && (fileId == null || isError);
  const name = data?.filename || "Open file";
  const href = getSpotFileUrl(uri, true);

  const link = unavailable ? (
    <span
      title="File not available"
      className={cn(
        "w-full min-w-0 truncate text-muted-foreground",
        onRemove && NAME_FADE_BEHIND_CONTROLS
      )}
    >
      {highlightText(uri, searchTerm, { tolerant: false })}
    </span>
  ) : (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={name}
      className={cn(
        "flex w-full min-w-0 items-center gap-1.5 text-primary hover:underline",
        // A mask, not an overlay: the row background changes when hovered or selected.
        onRemove && NAME_FADE_BEHIND_CONTROLS
      )}
    >
      {typeIcon ? (
        <TypeIcon contentType={data?.content_type ?? ""} />
      ) : (
        <FileText className="size-3.5 shrink-0" />
      )}
      <span className="truncate">
        {highlightText(name, searchTerm, { tolerant: false })}
      </span>
      {!onRemove && (
        <ExternalLink className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-60 group-focus-within:opacity-60" />
      )}
    </a>
  );

  if (!onRemove) return link;

  return (
    <span className="group/file relative flex w-full min-w-0 items-center">
      {link}
      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center gap-0.5 opacity-0 transition-opacity group-hover/file:opacity-100 group-focus-within/file:opacity-100">
        {/* Decorative: clicks fall through to the name link, which is the real control. */}
        {!unavailable && (
          <span className={CONTROL} aria-hidden>
            <ExternalLink className="size-3.5" />
          </span>
        )}
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove file"
          title="Remove file"
          className={cn(
            CONTROL,
            "hover:text-destructive group-hover/file:pointer-events-auto group-focus-within/file:pointer-events-auto"
          )}
        >
          <Trash2 className="size-3.5" />
        </button>
      </span>
    </span>
  );
}

function TypeIcon({ contentType }: { contentType: string }) {
  const { Icon } = pickIcon(contentType);
  return <Icon className="size-3.5 shrink-0 text-muted-foreground" />;
}
